import './add-item-form.scss'

import { defineComponent } from 'vue'
import { debounce } from 'lodash'
import { first } from 'rxjs/operators'
import { faCircleNotch, faUndo } from '@fortawesome/free-solid-svg-icons'

import { Item, MovementTypes, LinkableItem } from '@skinsoft/types'
import { ItemDataStore } from '@skinsoft/entity'
import { reactiveVueMixin } from '@skinsoft/reactive-vue'
import { BarcodeSubscriber, ItemCard } from '@skinsoft/ui'
import { BarcodeReader } from '../../../../ui'

export interface Values {
    location: string;
    identifier: string;
}

declare const config
const ITEM_SEARCH_TYPES: string[] = config.item_search_types

export const AddItemForm = defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        sBarcodeReader: BarcodeReader,
        sBarcodeSubscriber: BarcodeSubscriber,
        sItemCard: ItemCard,
    },
    props: ['onSubmit', 'initialValues', 'movementType', 'itemsIdsToExclude', 'canChainAdd'],
    template: require('./add-item-form.html'),
    data() {
        return {
            item: null as any as Item,

            MovementTypes,

            searchPending: false,
            debounce,
            errorMessage: "",
            submitting: false,

            INPUT_FILTER_IDENTIFIER: "add-item-search-query-input",

            // Icons
            undoIcon: faUndo,
            loadingIcon: faCircleNotch,
        }
    },
    methods: {
        async validate(values): Promise<any> {
            // need to be a promise to avoid conflict with $t mixin
            const errors = {} as any;
            const errorMessage = this.$t('entity.addItem.form.id.notFoundError') as string;

            if (!values.id) {
                this.errorMessage = this.$t('entity.addItem.form.id.emptyError') as string;
            }

            if (values.id) {
                try {
                    this.searchPending = true;
                    const matchingSearchResult = await this.$itemDataStore.searchOneWithText(values.id, ITEM_SEARCH_TYPES)

                    const searchResultId = ItemDataStore.getItemSearchResultId(matchingSearchResult);
                    if (this.itemsIdsToExclude && this.itemsIdsToExclude.find(linkableItem => linkableItem.linkableItem === searchResultId)) {
                        this.errorMessage = this.$t('entity.addItem.form.id.alreadyAdded') as string;
                        this.item = null as any;
                    } else {
                        this.item = await this.$itemDataStore.fetchOne(searchResultId).pipe(first()).toPromise();
                        const form = (this.$refs.form as any).form;
                        // Updating hidden fields, without re-performing a validation
                        // @ts-ignore
                        form.getState().values.itemId = this.item.id;
                        // @ts-ignore
                        form.getState().values.itemType = this.item.type;
                    }
                    this.searchPending = false;

                } catch (error) {
                    this.searchPending = false;
                    this.errorMessage = errorMessage;
                    this.item = null as any;
                }
            }
            return errors;
        },

        onBarcodeReaderInput(input: string) {
            const form = (this.$refs.form as any).form;

            form.batch(() => {
                const idState = form.getFieldState('id');
                const fieldName = 'id';
                form.change(fieldName, input);
                form.mutators.setFieldTouched(fieldName, true);
            });
        },

        resetSearch() {
            this.item = null as any;
            const form = (this.$refs.form as any).form;
            form.change('id', "");
            form.mutators.setFieldTouched("id", true);
            this.searchPending = false;
        },

        validateSelection() {
            this.$emit('item-selected', this.item)
            if (this.canChainAdd) {
                this.resetSearch()
            } else {
                this.submitting = true;
            }
        },
    },
})
